import React, { useState, useEffect } from "react";
import {
  Box, Typography, Chip, Dialog, DialogTitle, DialogContent, 
  IconButton, Tooltip, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, TablePagination, TextField, InputAdornment 
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchFolderReport } from "../../api/documentApi";
import Loading from "../Loading";
import FolderRecordsModal from "./FolderRecordsModal";
import { IoMdClose, IoMdSearch } from "react-icons/io";
import { FaFolder, FaFileAlt, FaCheckCircle, FaExclamationCircle, FaHourglassHalf, FaChartPie, FaEye } from "react-icons/fa";
import "./FolderReport.css";

const statusConfig = {
  Processed: { color: "#10b981", bg: "#ecfdf5", icon: <FaCheckCircle /> },
  "Pending Review": { color: "#f59e0b", bg: "#fffbeb", icon: <FaHourglassHalf /> },
  Validated: { color: "#3b82f6", bg: "#eff6ff", icon: <FaCheckCircle /> },
  "Validation Failed": { color: "#ef4444", bg: "#fef2f2", icon: <FaExclamationCircle /> },
};

const catLabels = {
  invoice: "Invoices",
  "doc_type:credit-note": "Credit Notes",
  "doc_type:other": "Other Docs",
  unlinked: "Unlinked",
  "doc_type:amendment": "Amendment",
  "doc_type:sof": "SOF",
};

const catShortLabels = {
  invoice: "Inv",
  "doc_type:credit-note": "CN",
  "doc_type:other": "Oth",
  unlinked: "Unl",
  "doc_type:amendment": "Amd",
  "doc_type:sof": "SOF",
};

const catColors = {
  invoice: "#6366f1",
  "doc_type:credit-note": "#ec4899",
  "doc_type:other": "#8b5cf6",
  unlinked: "#94a3b8",
  "doc_type:amendment": "#f59e0b",
  "doc_type:sof": "#10b981",
};

const FolderReport = () => {
  const [detailFolder, setDetailFolder] = useState(null);
  const [animated, setAnimated] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [searchQuery, setSearchQuery] = useState("");
  const [drilldown, setDrilldown] = useState(null);
  const [selectedFolderForRecords, setSelectedFolderForRecords] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["folderReport"],
    queryFn: fetchFolderReport,
  });

  useEffect(() => {
    if (data) setTimeout(() => setAnimated(true), 50);
  }, [data]);

  const folders = data?.data?.folders || [];
  const summary = data?.data?.summary || {};

  if (isLoading) return <Loading />;

  const totalDocs = summary.total_documents || 0;
  const processed = summary.hub_status?.Processed || 0;
  const pending = summary.hub_status?.["Pending Review"] || 0;
  const validated = summary.hub_status?.Validated || 0;
  const issues = (summary.hub_status?.["Validation Failed"] || 0);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredFolders = folders.filter(f => 
    !searchQuery || 
    (f.name && f.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const paginatedFolders = filteredFolders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className={`fr-container ${animated ? "fr-animated" : ""}`}>
      {/* ── Top Summary Row ── */}
      <div className="fr-top-summary-row">
        <div className="fr-quick-stats">
          <div className="fr-mini-stat" style={{ alignItems: 'center' }}>
            <span className="fr-mini-val" style={{color: '#6366f1', fontSize: '28px'}}>{totalDocs}</span>
            <span className="fr-mini-lbl" style={{ fontSize: '12px' }}>Total Files</span>
          </div>
          <div className="fr-mini-divider" style={{ height: '40px' }}></div>
          
          <div className="fr-hub-stats-group">
            <div className="fr-hub-stats-header">
              <span className="fr-hub-indicator"></span> Document Hub Activity
            </div>
            <div className="fr-hub-stats-content">
              <div className="fr-hub-stat" onClick={() => setDrilldown({ type: 'status', key: 'Processed', label: 'Processed' })} title="Click to see folders">
                <span className="fr-hub-val" style={{color: '#10b981'}}>{processed}</span>
                <span className="fr-hub-lbl">Processed</span>
              </div>
              <div className="fr-hub-stat" onClick={() => setDrilldown({ type: 'status', key: 'Pending Review', label: 'Pending' })} title="Click to see folders">
                <span className="fr-hub-val" style={{color: '#f59e0b'}}>{pending}</span>
                <span className="fr-hub-lbl">Pending</span>
              </div>
              <div className="fr-hub-stat" onClick={() => setDrilldown({ type: 'status', key: 'Validated', label: 'Validated' })} title="Click to see folders">
                <span className="fr-hub-val" style={{color: '#3b82f6'}}>{validated}</span>
                <span className="fr-hub-lbl">Validated</span>
              </div>
              <div className="fr-hub-stat" onClick={() => setDrilldown({ type: 'status', key: 'Validation Failed', label: 'Failed' })} title="Click to see folders">
                <span className="fr-hub-val" style={{color: '#ef4444'}}>{issues}</span>
                <span className="fr-hub-lbl">Failed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Category Bar */}
        {summary.by_category && (
          <div className="fr-cat-inline">
            <div className="fr-cat-inline-header">
              <span className="fr-cat-inline-title">Category Distribution</span>
              <div className="fr-cat-inline-legend">
                {Object.entries(summary.by_category).map(([key, count]) => (
                  <span key={key} className="fr-inline-legend-item">
                    <span className="fr-inline-dot" style={{ backgroundColor: catColors[key] || "#cbd5e1" }}></span>
                    <Tooltip title={`Click to see folders with ${catLabels[key] || key}`} arrow placement="top">
                      <span style={{ cursor: 'pointer' }} onClick={() => setDrilldown({ type: 'category', key, label: catLabels[key] || key })}>
                        {catShortLabels[key] || key} <strong style={{color: '#1e293b'}}>{count}</strong>
                      </span>
                    </Tooltip>
                  </span>
                ))}
              </div>
            </div>
            <div className="fr-cat-bar-container-inline">
              {Object.entries(summary.by_category).map(([key, count]) => {
                if (count === 0) return null;
                const pct = (count / totalDocs) * 100;
                return (
                  <Tooltip key={key} title={`${catLabels[key] || key}: ${count} (${pct.toFixed(1)}%)`} arrow>
                    <div 
                      className="fr-cat-segment" 
                      style={{ width: `${pct}%`, backgroundColor: catColors[key] || "#cbd5e1" }}
                    />
                  </Tooltip>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Folders Table ── */}
      <div className="fr-table-section">
        <div className="fr-table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="fr-table-title">
            <FaFolder color="#7C2EA0" /> Folder Details
          </div>
          <TextField
            size="small"
            placeholder="Search folders..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IoMdSearch color="#94a3b8" />
                </InputAdornment>
              ),
              sx: {
                bgcolor: "#fff",
                borderRadius: "8px",
                "& fieldset": { borderColor: "#e2e8f0" },
              }
            }}
            sx={{ width: 300 }}
          />
        </div>
        
        <TableContainer className="fr-table-container" component={Paper} elevation={0} sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small" aria-label="folders table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: '#475569', minWidth: 250, py: 1 }}>Folder Name</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569', width: 100, textAlign: 'center', py: 1 }}>Files</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569', width: 180, py: 1 }}>Progress</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569', maxWidth: 150, py: 1 }}>
                  <Tooltip title="Document hub status breakdown" placement="top" arrow>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'help' }}>
                      Document hub status breakdown
                    </div>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569', width: 350, py: 1 }}>Categories</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569', width: 80, textAlign: 'center', py: 1 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedFolders.map((folder) => {
                const fileCount = folder.file_count || 0;
                const processedCount = folder.hub_status?.Processed || 0;
                const progressPct = fileCount > 0 ? (processedCount / fileCount) * 100 : 0;
                
                return (
                  <TableRow hover key={folder.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <div className="fr-folder-row-title">
                        <div className="fr-folder-name-wrap">
                          <FaFolder size={14} color="#94a3b8" />
                          <span className="fr-folder-name">{folder.name}</span>
                          {folder.is_default === 1 && <span className="fr-default-badge">Default</span>}
                        </div>
                        {folder.description && (
                          <Tooltip title={folder.description} placement="bottom-start">
                            <div className="fr-folder-desc">{folder.description}</div>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    
                    <TableCell align="center">
                      <span style={{ fontWeight: 700, color: '#334155', fontSize: '15px' }}>{fileCount}</span>
                    </TableCell>
                    
                    <TableCell>
                      <div className="fr-table-progress-wrap">
                        <div className="fr-table-progress-bar">
                          <div className="fr-table-progress-fill" style={{ width: `${progressPct}%` }}></div>
                        </div>
                        <span className="fr-table-progress-pct">{progressPct.toFixed(0)}%</span>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="fr-status-bubbles">
                        {folder.hub_status && Object.keys(folder.hub_status).length > 0 ? (
                          Object.entries(folder.hub_status).map(([s, c]) => {
                            const sc = statusConfig[s] || { color: "#64748b", bg: "#f1f5f9", icon: <FaFileAlt /> };
                            return (
                              <div 
                                key={s} 
                                className="fr-status-bubble" 
                                style={{ background: sc.bg, color: sc.color, cursor: 'pointer' }}
                                onClick={() => setSelectedFolderForRecords({ folder, category: null, hubStatus: s })}
                              >
                                {sc.icon} {s}: {c}
                              </div>
                            );
                          })
                        ) : (
                          <span style={{ fontSize: '12px', color: '#94a3b8' }}>No activity</span>
                        )}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="fr-cat-chips">
                        {folder.by_category && Object.entries(folder.by_category).map(([key, count]) => (
                          <Tooltip key={key} title={catLabels[key] || key} arrow placement="top">
                            <span 
                              className="fr-cat-chip" 
                              style={{ 
                                backgroundColor: `${catColors[key] || '#94a3b8'}15`, 
                                color: catColors[key] || '#475569', 
                                borderColor: `${catColors[key] || '#cbd5e1'}40`,
                                cursor: 'pointer'
                              }}
                              onClick={() => setSelectedFolderForRecords({ folder, category: key, hubStatus: null })}
                            >
                              {catShortLabels[key] || key}: <strong>{count}</strong>
                            </span>
                          </Tooltip>
                        ))}
                      </div>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Tooltip title="View Detailed Breakdown">
                        <IconButton size="small" onClick={() => setDetailFolder(folder)} sx={{ color: '#7C2EA0' }}>
                          <FaEye size={16} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {folders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5, color: '#64748b' }}>
                    No folders found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100, 200]}
          component="div"
          count={filteredFolders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: '1px solid #f1f5f9' }}
        />
      </div>

      {/* ── Detail Dialog ── */}
      <Dialog open={!!detailFolder} onClose={() => setDetailFolder(null)} maxWidth="sm" fullWidth>
        {detailFolder && (
          <>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 2, borderBottom: '1px solid #f1f5f9' }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, fontSize: "18px", fontWeight: 700, color: '#1e293b' }}>
                <FaFolder color="#7C2EA0" />
                {detailFolder.name}
              </Box>
              <IconButton size="small" onClick={() => setDetailFolder(null)}>
                <IoMdClose size={20} />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3, pt: 3 }}>
              {detailFolder.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: "13px", lineHeight: 1.6, bgcolor: '#f8fafc', p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <strong>Description:</strong><br />
                  {detailFolder.description}
                </Typography>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
                <div className="fr-modal-stat-box" style={{ cursor: 'pointer' }} onClick={() => setSelectedFolderForRecords({ folder: detailFolder, category: null, hubStatus: null })}>
                  <div className="fr-modal-stat-val">{detailFolder.file_count || 0}</div>
                  <div className="fr-modal-stat-lbl">Total Files</div>
                </div>
                <div className="fr-modal-stat-box" style={{ background: '#ecfdf5', borderColor: '#d1fae5', cursor: 'pointer' }} onClick={() => setSelectedFolderForRecords({ folder: detailFolder, category: null, hubStatus: 'Processed' })}>
                  <div className="fr-modal-stat-val" style={{ color: '#10b981' }}>{detailFolder.hub_status?.Processed || 0}</div>
                  <div className="fr-modal-stat-lbl">Processed</div>
                </div>
                <div className="fr-modal-stat-box" style={{ background: '#fffbeb', borderColor: '#fef3c7', cursor: 'pointer' }} onClick={() => setSelectedFolderForRecords({ folder: detailFolder, category: null, hubStatus: 'Pending Review' })}>
                  <div className="fr-modal-stat-val" style={{ color: '#f59e0b' }}>{detailFolder.hub_status?.["Pending Review"] || 0}</div>
                  <div className="fr-modal-stat-lbl">Pending</div>
                </div>
                <div className="fr-modal-stat-box" style={{ background: '#eff6ff', borderColor: '#dbeafe', cursor: 'pointer' }} onClick={() => setSelectedFolderForRecords({ folder: detailFolder, category: null, hubStatus: 'Validated' })}>
                  <div className="fr-modal-stat-val" style={{ color: '#3b82f6' }}>{detailFolder.hub_status?.Validated || 0}</div>
                  <div className="fr-modal-stat-lbl">Validated</div>
                </div>
                <div className="fr-modal-stat-box" style={{ background: '#fef2f2', borderColor: '#fee2e2', cursor: 'pointer' }} onClick={() => setSelectedFolderForRecords({ folder: detailFolder, category: null, hubStatus: 'Validation Failed' })}>
                  <div className="fr-modal-stat-val" style={{ color: '#ef4444' }}>{detailFolder.hub_status?.["Validation Failed"] || 0}</div>
                  <div className="fr-modal-stat-lbl">Failed</div>
                </div>
              </div>

              {detailFolder.category_breakdown && (
                <div>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: '#334155' }}>Category Breakdown</Typography>
                  {Object.entries(detailFolder.category_breakdown).map(([cat, info]) => (
                    <div className="fr-modal-breakdown-row" key={cat}>
                      <span className="fr-modal-breakdown-name">{catLabels[cat] || cat}</span>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip label={`Total: ${info.total}`} size="small" sx={{ fontWeight: 700, fontSize: "12px", cursor: 'pointer' }} onClick={() => setSelectedFolderForRecords({ folder: detailFolder, category: cat, hubStatus: null })} />
                        {info.direct_upload > 0 && (
                          <Chip label={`Direct: ${info.direct_upload}`} size="small" sx={{ fontSize: "11px", bgcolor: "#e0f2fe", color: "#0284c7", fontWeight: 600 }} />
                        )}
                        {info.hub_status && Object.entries(info.hub_status).map(([s, c]) => {
                          const sc = statusConfig[s] || { color: "#64748b", bg: "#f1f5f9" };
                          return (
                            <Chip key={s} label={`${s}: ${c}`} size="small"
                              sx={{ fontSize: "11px", bgcolor: sc.bg, color: sc.color, fontWeight: 700, border: `1px solid ${sc.color}30`, cursor: 'pointer' }}
                              onClick={() => setSelectedFolderForRecords({ folder: detailFolder, category: cat, hubStatus: s })}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* ── Drilldown Dialog ── */}
      <Dialog open={!!drilldown} onClose={() => setDrilldown(null)} maxWidth="sm" fullWidth>
        {drilldown && (
          <>
            <DialogTitle sx={{ py: 2, borderBottom: '1px solid #f1f5f9' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={700} color="#1e293b">
                  Folders with {drilldown.label}
                </Typography>
                <IconButton size="small" onClick={() => setDrilldown(null)}>
                  <IoMdClose size={20} />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1.5 }}>Folder Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', width: 100, textAlign: 'center', py: 1.5 }}>Count</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', width: 80, textAlign: 'center', py: 1.5 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {folders
                      .map(f => {
                        let count = 0;
                        if (drilldown.type === 'status') count = f.hub_status?.[drilldown.key] || 0;
                        else if (drilldown.type === 'category') count = f.by_category?.[drilldown.key] || 0;
                        return { folder: f, count };
                      })
                      .filter(item => item.count > 0)
                      .sort((a,b) => b.count - a.count)
                      .map((item, idx) => (
                        <TableRow 
                          key={idx} 
                          hover 
                          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f8fafc' } }}
                          onClick={() => setSelectedFolderForRecords({
                            folder: item.folder,
                            category: drilldown.type === 'category' ? drilldown.key : null,
                            hubStatus: drilldown.type === 'status' ? drilldown.key : null
                          })}
                        >
                          <TableCell sx={{ color: '#334155', fontWeight: 500, py: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FaFolder color="#cbd5e1" /> {item.folder.name}
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, color: '#0f172a', py: 1.5 }}>{item.count}</TableCell>
                          <TableCell align="center" sx={{ py: 1.5 }}>
                            <Tooltip title="View Files">
                              <IconButton size="small" sx={{ color: '#7C2EA0' }}>
                                <FaEye size={16} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
          </>
        )}
      </Dialog>

      <FolderRecordsModal
        open={!!selectedFolderForRecords}
        onClose={() => setSelectedFolderForRecords(null)}
        folderId={selectedFolderForRecords?.folder?.id}
        folderName={selectedFolderForRecords?.folder?.name}
        category={selectedFolderForRecords?.category}
        hubStatus={selectedFolderForRecords?.hubStatus}
      />
    </div>
  );
};

export default FolderReport;
